import LightningFS from "@isomorphic-git/lightning-fs"
import * as IPFSCore from "ipfs-core"
import {IPFS} from "ipfs-core"
import OrbitDB from "orbit-db"
import Identities, {Identity} from "orbit-db-identity-provider"
import {useCallback, useEffect, useRef, useState} from "react"
import {AppConfig} from "../App.config"
import {Ed25519IdentityProvider} from "../classes/orbit-identity-provider"
import {convertEd25519ToIPFSKey} from "../functions/auth"
import {Keys} from "../interfaces/keys"

export const useInitBackend = (keys?: Keys | null) => {
  const [ipfs, setIPFS] = useState<IPFS>()
  const [orbit, setOrbit] = useState<OrbitDB>()
  const [fs, setFS] = useState<LightningFS>()
  const timer = useRef<NodeJS.Timer>()

  const initIPFS = useCallback(async (keys: Keys | null): Promise<IPFS> => {
    const ipfsConfig = AppConfig.ipfsConfig
    if (keys != null) {
      ipfsConfig.init = {
        algorithm: "Ed25519",
        privateKey: convertEd25519ToIPFSKey(keys.webIpfs)
      }
    }
    const ipfs = await IPFSCore.create(ipfsConfig)
    await ipfs.swarm.connect(AppConfig.ipfsWebSocket)
    return ipfs
  }, [])

  const initOrbitDB = useCallback(async (ipfs: IPFS, keys: Keys | null): Promise<OrbitDB> => {
    Identities.addIdentityProvider(Ed25519IdentityProvider)
    let identity: Identity | undefined = undefined
    if (keys != null) {
      identity = await Identities.createIdentity({
        type: "Ed25519IdentityProvider",
        keypair: keys.webIpfs
      })
    }
    return await OrbitDB.createInstance(
      ipfs as any,
      {identity}
    )
  }, [])

  const initAll = useCallback(async (keys: Keys | null) => {
    console.log("DEBUG: initIPFSOrbit begins")

    const ipfs = await initIPFS(keys)
    const orbitDB = await initOrbitDB(ipfs, keys)
    const fs = new LightningFS()

    setIPFS(ipfs)
    setOrbit(orbitDB)
    setFS(fs)

    console.log("DEBUG: initIPFSOrbit ends")
  }, [initIPFS, initOrbitDB])

  useEffect(() => {
    // Only init IPFS when auth is checked (i.e. keys is initialized)
    if (keys === undefined) {
      return
    }
    initAll(keys).then()
  }, [keys, initAll])

  // Send heartbeat
  useEffect(() => {
    if (!ipfs) {
      return
    }

    timer.current = setInterval(async () => {
      await ipfs.swarm.connect(AppConfig.ipfsWebSocket)
      const msg = new TextEncoder().encode("web-heartbeat")
      await ipfs.pubsub.publish(AppConfig.orbitTopic, msg)
    }, 5000)

    return () => {
      if (!timer.current) {
        return
      }
      console.log("heartbeat cleared")
      clearInterval(timer.current)
    }
  }, [ipfs])

  return {
    ipfs,
    orbit,
    fs,
  }
}
